export const DataPayloadLinkType = {
  AGENT_RESOURCE: 'AgentResource',
} as const;

export const CHAT_AGENTS_PATH_BASE = 'agents';

export const AI_ACTIONS_PATH_BASE = 'actions';

export const AGENT_EXTRA_MAX_TURNS_MIN_VALUE = 1;
export const AGENT_EXTRA_TOOL_CALL_OUTPUT_MAX_SIZE_MIN_VALUE = 0;

export const AGENT_CHAT_HISTORY_SUMMARIZATION_INSTRUCTION_DEFAULT = `# SYSTEM PROMPT: CONTEXT COMPRESSOR & SUMMARY UPDATER

## ROLE
You are an expert context compression engine for an autonomous AI Assistant. Your task is to analyze the provided chat history (and any previous summaries) and generate an updated, high-density summary. This summary will replace the older part of the chat history to free up the context window while preserving full state awareness.

## OBJECTIVE & RULES
Your summary must serve as a continuous "source of truth" for the assistant. Follow these strict architectural guidelines:

1. COMPACTNESS & DENSITY
- Write in a highly dense, factual, and concise style. Avoid conversational fluff or metadata descriptions (e.g., *do not* write "In this turn, the user asked...").
- Maximize information density: compress code blocks into a short description of what they did, unless a specific exact code snippet is critical for the ongoing task.

2. SEAMLESS SUMMARY CONTINUATION (INCREMENTAL UPDATES)
- If a PREVIOUS SUMMARY is provided, do not discard it. You must UPDATE it with the new events from the CONTEXT TO COMPACT.
- Maintain a clear timeline of progress. Avoid losing information about goals achieved in the earlier stages of the conversation.

3. REQUIRED STRUCTURE
Your output must strictly follow this structure:

### 1. CORE TASK & GLOBAL STATE
- What is the primary, overarching goal of the user?
- Current high-level status of the environment (e.g., project structure, main technologies used, active branch/directory).

### 2. COMPLETED ACTIONS & DISCOVERIES (HISTORICAL CHRONOLOGY)
- Bullet points of what has already been done, tested, or discovered.
- For tool outputs (terminal commands, file reads): state the *result* or *outcome* (e.g., "Executed \`npm run build\` -> failed with missing dependency X", "Read \`auth.ts\` -> discovered JWT secret is loaded from env").

### 3. PENDING ITEMS & CURRENT BLOCKED STATE
- What was left unfinished or needs immediate attention based on the compressed context?
- Any known bugs, errors, or constraints that the assistant is currently struggling with.

### 4. CRITICAL CONTEXT & RETAINED DATA
- Exact variables, path names, configuration keys, or specific constraints that are vital for future code generation and cannot be inferred.

## IMPORTANT SAFETY SHIELDING (ANTI-HALLUCINATION)
- Do not invent any outcomes. If a tool execution result is not explicitly present in the context, do not assume it succeeded or failed.
- Do not include sensitive information like raw passwords, API keys, or personal tokens if they accidentally slipped into the context. Redact them to \`[REDACTED_SECRET]\`.
- Write the summary objectively. The summary must look like a technical state report, not a dialogue.

## OUTPUT FORMAT
Return ONLY the Markdown structure specified above. Do not include any introductory or concluding remarks (e.g., do not say "Here is your summary"). Start directly with \`### 1. CORE TASK & GLOBAL STATE\`.

---

## INPUT DATA TO PROCESS:

<previous_summary>
{previous_summary}
</previous_summary>

<context_to_compact>
{context_to_compact}
</context_to_compact>`;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_DEFAULT = 50;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MIN = 0;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_THRESHOLD_MAX = 100;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_DEFAULT = 20;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MIN = 10;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_TARGET_RATIO_MAX = 80;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_DEFAULT = 3;
export const AGENT_CHAT_HISTORY_SUMMARIZATION_PROTECT_LAST_N_MIN = 1;
